import React, {useCallback, useEffect, useState} from 'react';
import {
    FiActivity,
    FiAlertCircle,
    FiCheckCircle,
    FiClock,
    FiEye,
    FiFilter,
    FiLoader,
    FiRefreshCw,
} from 'react-icons/fi';
import {getErrorMessage, resourcesApi} from '../services/api';
import './LlmLogs.css';

const normalizeCollection = (data) => Array.isArray(data) ? data : (data?.results || data?.items || []);

const statusLabels = {
    pending: 'در انتظار',
    started: 'در حال اجرا',
    success: 'موفق',
    failure: 'ناموفق',
    failed: 'ناموفق',
};

const operationLabels = {
    graph: 'ساخت گراف',
    generate_questions: 'تولید سؤال',
    questions_generate: 'تولید سؤال',
    document_generate_questions: 'تولید سؤال سند',
};

const statusIcon = (status = '') => {
    const normalized = status.toLowerCase();
    if (['success', 'completed', 'done'].includes(normalized)) return <FiCheckCircle/>;
    if (['failure', 'failed', 'error'].includes(normalized)) return <FiAlertCircle/>;
    if (['pending', 'started', 'running'].includes(normalized)) return <FiClock/>;
    return <FiActivity/>;
};

const formatDate = (value) => {
    if (!value) return 'زمان نامشخص';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('fa-IR', {dateStyle: 'short', timeStyle: 'short'}).format(date);
};

const LlmLogs = () => {
    const [filters, setFilters] = useState({operation: '', status: '', model: '', limit: 25});
    const [logs, setLogs] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const setFilter = (name, value) => setFilters((current) => ({...current, [name]: value}));

    const loadLogs = useCallback(async () => {
        setIsLoading(true);
        setMessage(null);
        try {
            const items = normalizeCollection(await resourcesApi.llmLogs(filters));
            setLogs(items);
            if (selectedLog && !items.some((item) => String(item.id) === String(selectedLog.id))) {
                setSelectedLog(null);
            }
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsLoading(false);
        }
    }, [filters, selectedLog]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const openDetail = async (log) => {
        setDetailLoading(true);
        setMessage(null);
        try {
            setSelectedLog(await resourcesApi.llmLogDetail(log.id));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <main className="llm-page">
            <section className="llm-hero app-page-header">
                <div>
                    <span><FiActivity/> پایش درخواست‌های هوش مصنوعی</span>
                    <h1>لاگ‌های LLM</h1>
                    <p>درخواست‌های تولید سؤال و ساخت گراف را بررسی کنید و شناسه لاگ لازم برای ادامه از checkpoint را بردارید.</p>
                </div>
                <button type="button" onClick={loadLogs} disabled={isLoading}>
                    <FiRefreshCw className={isLoading ? 'llm-spin' : ''}/> بروزرسانی
                </button>
            </section>

            {message && <div className={`llm-message ${message.type}`}>{message.text}</div>}

            <section className="llm-filters" aria-label="فیلتر لاگ‌های LLM">
                <label>
                    <span><FiFilter/> عملیات</span>
                    <input value={filters.operation} placeholder="graph یا generate_questions"
                        onChange={(event) => setFilter('operation', event.target.value)}/>
                </label>
                <label>
                    <span>وضعیت</span>
                    <select value={filters.status} onChange={(event) => setFilter('status', event.target.value)}>
                        <option value="">همه</option>
                        <option value="pending">pending</option>
                        <option value="started">started</option>
                        <option value="success">success</option>
                        <option value="failure">failure</option>
                    </select>
                </label>
                <label>
                    <span>مدل</span>
                    <input value={filters.model} placeholder="نام مدل"
                        onChange={(event) => setFilter('model', event.target.value)}/>
                </label>
                <label>
                    <span>تعداد</span>
                    <input type="number" min="1" max="100" value={filters.limit}
                        onChange={(event) => setFilter('limit', event.target.value)}/>
                </label>
            </section>

            <div className="llm-layout">
                <section className="llm-list">
                    {isLoading ? (
                        <div className="llm-empty"><FiLoader className="llm-spin"/><p>در حال دریافت لاگ‌ها...</p></div>
                    ) : logs.length ? logs.map((log) => {
                        const status = String(log.status || log.state || '').toLowerCase();
                        const operation = log.operation || log.operation_name || '';
                        return (
                            <article className={`llm-log ${status}`} key={log.id}>
                                <div className="llm-log-icon">{statusIcon(status)}</div>
                                <div className="llm-log-main">
                                    <div>
                                        <strong>{operationLabels[operation] || operation || 'عملیات نامشخص'}</strong>
                                        <small>LLM log: {log.id}</small>
                                    </div>
                                    <p>{log.model || 'مدل نامشخص'} · {statusLabels[status] || status || 'وضعیت نامشخص'} · {formatDate(log.created_at || log.started_at)}</p>
                                </div>
                                <button type="button" onClick={() => openDetail(log)} disabled={detailLoading}>
                                    <FiEye/> جزئیات
                                </button>
                            </article>
                        );
                    }) : (
                        <div className="llm-empty"><FiActivity/><p>لاگی با این فیلترها پیدا نشد.</p></div>
                    )}
                </section>

                <aside className="llm-detail">
                    <header>
                        <h2>جزئیات لاگ</h2>
                        {detailLoading && <FiLoader className="llm-spin"/>}
                    </header>
                    {selectedLog ? (
                        <dl>
                            <div><dt>شناسه</dt><dd>{selectedLog.id}</dd></div>
                            <div><dt>عملیات</dt><dd>{selectedLog.operation || selectedLog.operation_name || '-'}</dd></div>
                            <div><dt>وضعیت</dt><dd>{selectedLog.status || selectedLog.state || '-'}</dd></div>
                            <div><dt>مدل</dt><dd>{selectedLog.model || '-'}</dd></div>
                            <div><dt>شروع</dt><dd>{formatDate(selectedLog.created_at || selectedLog.started_at)}</dd></div>
                            <div><dt>پایان</dt><dd>{formatDate(selectedLog.finished_at || selectedLog.completed_at)}</dd></div>
                            <div className="wide"><dt>خطا</dt><dd>{selectedLog.error || selectedLog.error_message || '-'}</dd></div>
                            <div className="wide"><dt>Checkpoint</dt><dd>{selectedLog.checkpoint || selectedLog.checkpoint_key || selectedLog.resume_from || '-'}</dd></div>
                        </dl>
                    ) : (
                        <p>یک لاگ را انتخاب کنید تا جزئیات و شناسه لازم برای resume را ببینید.</p>
                    )}
                </aside>
            </div>
        </main>
    );
};

export default LlmLogs;
